import { MutableRefObject, useEffect, useRef } from "react";
import CompetitionItem from "./competitionItem";
import { CompetitionCategory, CompetitionCategoryResponse } from "../../redux/features/categories/slice";
import { useCompetitionsCategoriesLoading } from "../../redux/features/categories/selectors";
import { DEVICE_RESOLUTION } from "../../app-constants";
import React from "react";
import { useIsRAFTEnabled } from "../../redux/features/header/selectors";
import { hasActiveSubscription } from "../../../helpers/hasActiveSubscription";
import { useGetCurrenciesQuery } from "../../redux/features/api/winners";

interface CompetitionListProps {
  LoadingComponent: React.ComponentType;
  WrapperComponent: React.ComponentType<{
    children: React.ReactNode;
    category: CompetitionCategory;
    containerRef?: MutableRefObject<HTMLDivElement>;
  }>;
  category: CompetitionCategory;
}

const CompetitionList = ({ LoadingComponent, WrapperComponent, category }: CompetitionListProps) => {
  const isLoading = useCompetitionsCategoriesLoading();
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: currenciesData } = useGetCurrenciesQuery();
  const isUserValidated = currenciesData?.isUserValidated ?? false;
  const isHomepage = location.pathname === "/";
  const isMobile = window.innerWidth >= DEVICE_RESOLUTION.Mobile && window.innerWidth <= DEVICE_RESOLUTION.Tablet;
  const isRAFEnabled = useIsRAFTEnabled();
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          const scrollByEvent = new CustomEvent("scroll-by-category", { detail: category.type });
          window.dispatchEvent(scrollByEvent);
        }
      },
      {
        threshold: isMobile ? 0.2 : 0.6,
      }
    );
    observer.observe(containerRef.current!);
    const onScrollToCategory = (e: CustomEvent<CompetitionCategoryResponse["type"]>) => {
      const header = document.getElementById("botb-header") || document.querySelector(".mainHeader");
      const categoryMenu = document.getElementById("category-menu");

      if (containerRef.current && header && categoryMenu) {
        const headerHeight = header.getBoundingClientRect().height;
        const categoryMenuHeight = categoryMenu.getBoundingClientRect().height;
        const elementPosition = containerRef.current.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - headerHeight - categoryMenuHeight - 16;
        if (e.detail === category.type) {
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }
    };
    window.addEventListener("scroll-to-category", onScrollToCategory);

    return () => {
      window.removeEventListener("scroll-to-category", onScrollToCategory);
      observer.disconnect();
    };
  }, [isMobile]);

  const reviveAdBanner = useRef();

  useEffect(() => {
    if (reviveAdBanner) {
      const reviveScript = document.createElement("script");
      reviveScript.src = "//www.botb.com/www/delivery/asyncjs.php";
      reviveScript.async = true;
      const adContainer = document.getElementById("script_ad_server");
      if (adContainer) {
        adContainer.appendChild(reviveScript);
      }
      return () => {
        if (adContainer) {
          adContainer.innerHTML = "";
        }
      };
    }
  }, [reviveAdBanner]);

  return (
    <>
      <div id={"script_ad_server"} className={"hidden"} />
      <div ref={reviveAdBanner} className={"hidden"} />

      {isLoading ? (
        <LoadingComponent />
      ) : (
        <WrapperComponent category={category} containerRef={containerRef}>
          {category.competitions.map((item, index) => {
            const requiredIndexPosition = 4;
            const itemsCount = category.competitions.length;
            const itemIndexExists = itemsCount > requiredIndexPosition;
            const isLastItem = index === itemsCount - 1;

            const shouldRenderAdBanner =
              category.type === "featured" &&
              ((itemIndexExists && index === requiredIndexPosition) || (!itemIndexExists && isLastItem));

            return (
              <React.Fragment key={item.CompetitionId}>
                {shouldRenderAdBanner && isHomepage && (
                  <>
                    {/*mobile wide*/}
                    <ins
                      data-revive-usersubscriber={hasActiveSubscription()}
                      data-revive-zoneid="18"
                      data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                      data-revive-source={"lobby_card01_mob"}
                      className={"lg:hidden [&_img]:size-full [&_img]:object-cover [&_img]:rounded-lg"}></ins>
                    {/*desktop wide*/}
                    <ins
                      data-revive-usersubscriber={hasActiveSubscription()}
                      data-revive-zoneid="17"
                      data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                      data-revive-source={"lobby_card01_desk"}
                      className={"max-lg:hidden [&_img]:size-full [&_img]:object-cover [&_img]:rounded-lg"}></ins>
                  </>
                )}

                <CompetitionItem category={category} item={item} isUserValidated={isUserValidated} />
              </React.Fragment>
            );
          })}
        </WrapperComponent>
      )}
    </>
  );
};

export default CompetitionList;
